import React, { useEffect, useState } from "react";
import { useSiteStore } from "../../stores/Assets/site";
import { useMenuStore } from "../../stores/Assets/menu";
import { useSpaceStore } from "../../stores/Assets/space";
import { useKitchenStore } from "../../stores/Assets/kitchen";
import { Button, Modal } from "reactstrap";
import {
    LayoutGrid,
    Search,
    ChevronDown,
    Loader2,
    Plus,
    Trash2,
    Edit2,
    QrCode,
    Trash
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import ManageSpacesView from "./components/ManageSpacesView";
import EditSpace from "../Locations/Spaces/Components/EditSpace";
import QRmodal from "../Locations/Spaces/Components/QRmodal";

const THEME_COLOR = '#c59253';

// Reusable styling for menu tags helper
const getMenuTagColor = (index) => {
    const colors = ['bg-primary', 'bg-success', 'bg-info', 'bg-warning', 'bg-danger'];
    return colors[index % colors.length];
};

const MySpaces = () => {
    const { t } = useTranslation();

    // We need sites with full menu structure to compare menu counts/IDs
    const { getSitesWithFullMenus, getSiteMenuFull } = useMenuStore();
    const { getSpaces, deleteSpace, pagination } = useSpaceStore();
    const { getKitchensBySite } = useKitchenStore();

    const [sitesData, setSitesData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [filterSiteId, setFilterSiteId] = useState(null);
    const [selectedSiteId, setSelectedSiteId] = useState(null);
    const [expandedSites, setExpandedSites] = useState([]);
    const [spacesBySite, setSpacesBySite] = useState({}); // { siteId: [spaces] }
    const [pageBySite, setPageBySite] = useState({}); // { siteId: pageNumber }
    const [hasMoreBySite, setHasMoreBySite] = useState({}); // { siteId: boolean }
    const [loadingSpaces, setLoadingSpaces] = useState({}); // { siteId: boolean }
    const [loadingMore, setLoadingMore] = useState({}); // { siteId: boolean }
    const [loadingAction, setLoadingAction] = useState({ type: null, id: null }); // { type: 'edit'|'delete', id: spaceId }

    // Modals state
    const [editSpaceData, setEditSpaceData] = useState(null);
    const [showEditSpaceModal, setShowEditSpaceModal] = useState(false);
    const [qrData, setQrData] = useState(null);
    const [showQrModal, setShowQrModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, spaceId: null, siteId: null });

    // Data for Edit Modal
    const [kitchenList, setKitchenList] = useState([]);
    const [menuList, setMenuList] = useState([]);

    // Fetch initial data (sites + menus)
    const fetchAllData = async () => {
        setIsLoading(true);
        const fullData = await getSitesWithFullMenus();
        if (fullData) {
            setSitesData(fullData);
            // Default select the first available site
            if (fullData.length > 0 && !filterSiteId) {
                setFilterSiteId(fullData[0].site_id);
            }
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    // Auto-expand the filtered site
    useEffect(() => {
        if (filterSiteId && !expandedSites.includes(filterSiteId)) {
            toggleSite(filterSiteId);
        }
    }, [filterSiteId, sitesData]);

    // Load more spaces logic
    const loadMoreSpaces = async (siteId, page = null) => {
        const isFirstPage = page === 1;
        const currentPage = page || (pageBySite[siteId] || 1) + 1;
        const perPage = 30;

        if (isFirstPage) {
            setLoadingSpaces(prev => ({ ...prev, [siteId]: true }));
        } else {
            setLoadingMore(prev => ({ ...prev, [siteId]: true }));
        }

        const spaces = await getSpaces(siteId, { page: currentPage, perPage });

        if (spaces) {
            setSpacesBySite(prev => ({
                ...prev,
                [siteId]: isFirstPage ? spaces : [...(prev[siteId] || []), ...spaces]
            }));
            setPageBySite(prev => ({ ...prev, [siteId]: currentPage }));

            // Determine if more pages exist using store pagination state
            const storePagination = useSpaceStore.getState().pagination;

            const updatedSpacesCount = isFirstPage ? spaces.length : (spacesBySite[siteId]?.length || 0) + spaces.length;
            const hasMore = storePagination ? updatedSpacesCount < storePagination?.totalItems : spaces.length === perPage;

            setHasMoreBySite(prev => ({ ...prev, [siteId]: hasMore }));
        }

        if (isFirstPage) {
            setLoadingSpaces(prev => ({ ...prev, [siteId]: false }));
        } else {
            setLoadingMore(prev => ({ ...prev, [siteId]: false }));
        }
    };

    // Toggle Site logic: Fetch spaces on expand if not present
    const toggleSite = async (siteId) => {
        const isCurrentlyExpanded = expandedSites.includes(siteId);

        if (!isCurrentlyExpanded) {
            setExpandedSites(prev => [...prev, siteId]); // Allow multiple - Expand immediately
            if (!spacesBySite[siteId]) {
                await loadMoreSpaces(siteId, 1);
            }
        } else {
            setExpandedSites(prev => prev.filter(id => id !== siteId));
        }
    };

    const filteredSites = sitesData?.filter(site => {
        const matchesSearch = site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            site.address?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filterSiteId ? site.site_id === filterSiteId : true;
        return matchesSearch && matchesFilter;
    });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 300, damping: 24 }
        }
    };

    // --- Modal Handlers ---

    const handleEditSpace = async (space, siteId) => {
        setLoadingAction({ type: 'edit', id: space.space_id });
        setEditSpaceData(null);
        // Prepare data for Edit Modal
        const kitchensRes = await getKitchensBySite(siteId, { page: 1, perPage: 100 });
        const menuRes = await getSiteMenuFull(siteId);

        setKitchenList(kitchensRes?.kitchens || []);
        if (menuRes && menuRes.menus) {
            // menuList expects objects with sites array if we follow EditSpace logic, 
            // but here we know the site. Let's adapt.
            // The EditSpace component filters menuList by siteId assuming menu.sites exists.
            // We need to shape it correctly or adjust EditSpace (but avoiding EditSpace changes if possible).
            // Actually currently EditSpace logic for menus:
            /*
               const filteredMenus = menuList.filter((menu) => {
                   if (!Array.isArray(menu?.sites)) return false;
                   return menu.sites.some((site) => parseInt(site.site_id) === parseInt(selectedSiteId));
               });
            */
            // Since we are fetching for a specific site, let's artificially add sites array to menus
            const menusWithSites = menuRes.menus.map(m => ({ ...m, sites: [{ site_id: siteId }] }));
            setMenuList(menusWithSites);
        } else {
            setMenuList([]);
        }

        setEditSpaceData({ ...space, site_id: siteId });
        setShowEditSpaceModal(true);
        setLoadingAction({ type: null, id: null });
    };

    const handleQRSpace = (space, site) => {
        setQrData({
            ...space,
            space_name: space.name,
            menu_site_image: site.image_url
            // Note: QRModal expects info?.menu_name or name, space_name, etc.
            // also key/encrypted. Space usually has a QR key.
            // If space doesn't have a linked menu, it might be tricky for standard QR logic unless it's a "Space QR"
            // QRModal logic: value={`${process.env.REACT_APP_QR_URL}/menu/${info?.qrKey || info?.encrypted}`}
        });
        setShowQrModal(true);
    };

    const handleDeleteSpace = (spaceId, siteId) => {
        setDeleteModal({ isOpen: true, spaceId, siteId });
    };

    const confirmDeleteSpace = async () => {
        if (deleteModal.spaceId && deleteModal.siteId) {
            setLoadingAction({ type: 'delete_confirm', id: deleteModal.spaceId });
            await deleteSpace(deleteModal.spaceId, deleteModal.siteId);
            // Refresh spaces
            const spaces = await getSpaces(deleteModal.siteId, { page: 1, perPage: 100 });
            setSpacesBySite(prev => ({ ...prev, [deleteModal.siteId]: spaces || [] }));
            setDeleteModal({ isOpen: false, spaceId: null, siteId: null });
            setLoadingAction({ type: null, id: null });
        }
    };

    // Helper to render Menu Tags for a space
    const renderMenuTags = (space, siteMenus) => {
        const totalMenus = siteMenus.length;
        const attachedIds = space.menu_ids || [];

        if (attachedIds.length === 0) {
            return <span className="badge bg-light text-muted border fw-normal" style={{ fontSize: '9px' }}>{t("No Menu")}</span>;
        }

        const validAttachedIds = attachedIds.filter(id => siteMenus.some(m => m.menu_id === id));

        if (validAttachedIds.length === totalMenus && totalMenus > 0) {
            return <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '10px' }}>{t("All Menus")}</span>;
        }

        const tags = siteMenus.filter(m => validAttachedIds.includes(m.menu_id));
        return (
            <div className="d-flex flex-wrap gap-1">
                {tags.map((menu, idx) => (
                    <span
                        key={menu.menu_id}
                        className="badge bg-white text-secondary border fw-normal"
                        style={{ fontSize: '9px' }}
                    >
                        {menu.name}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="bg-light min-vh-100 font-sans">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
                body { font-family: 'Outfit', sans-serif !important; }
                .font-sans { font-family: 'Outfit', sans-serif !important; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.02); }
                .group-header {
                    background-color: var(--vz-card-bg-custom, var(--vz-card-bg, #fff));
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .group-header:hover {
                    background-color: var(--vz-light, #f8f9fa);
                }
                .custom-scroll::-webkit-scrollbar { width: 6px; }
                .custom-scroll::-webkit-scrollbar-thumb { background-color: rgba(0,0,0,0.1); border-radius: 10px; }
                
                /* Dark Mode Refinements */
                [data-layout-mode="dark"] .bg-light { background-color: var(--vz-body-bg) !important; }
                [data-layout-mode="dark"] .text-dark { color: var(--vz-heading-color) !important; }
                [data-layout-mode="dark"] .badge.bg-white { background-color: var(--vz-light) !important; color: var(--vz-body-color) !important; border-color: var(--vz-border-color) !important; }
                [data-layout-mode="dark"] .form-control, [data-layout-mode="dark"] .form-select { background-color: var(--vz-input-bg) !important; color: var(--vz-body-color) !important; border-color: var(--vz-border-color) !important; }
                [data-layout-mode="dark"] .group-header { border-bottom: 1px solid var(--vz-border-color) !important; }
                [data-layout-mode="dark"] .bg-card-theme { background-color: var(--vz-card-bg-custom, var(--vz-card-bg, #fff)) !important; }
                
                .bg-card-theme { background-color: #fff; }
                .form-select:focus { box-shadow: none; border-color: var(--vz-primary); }
            `}</style>

            <div className="container-fluid px-4 ">
                {/* Header */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-3">
                    <div>
                        <h1 className="fw-bold mb-1" style={{ color: 'var(--vz-heading-color, #2b2b2b)' }}>{t("My Spaces")}</h1>
                        <p className="text-muted mb-0">{t("Manage spaces (tables, desks, etc.)")}</p>
                    </div>

                    <div className="d-flex gap-3 align-items-center">
                        {/* Site Filter */}
                        <div className="position-relative d-none d-md-block" style={{ width: '220px' }}>
                            <LayoutGrid size={16} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                            <select
                                className="form-select rounded-pill border-0 shadow-sm ps-5 small"
                                style={{ appearance: 'none', cursor: 'pointer', height: '38px', fontSize: '14px', backgroundColor: 'var(--vz-input-bg, #fff)' }}
                                value={filterSiteId || ""}
                                onChange={(e) => setFilterSiteId(e.target.value ? parseInt(e.target.value) : null)}
                            >
                                <option value="">{t("All Branches")}</option>
                                {sitesData?.map(site => (
                                    <option key={site.site_id} value={site.site_id}>{site.name}</option>
                                ))}
                            </select>
                            <ChevronDown size={14} className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted pointer-events-none" />
                        </div>

                        {/* Search Input */}
                        <div className="position-relative d-none d-md-block" style={{ width: '300px' }}>
                            <Search size={16} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                            <input
                                type="text"
                                className="form-control rounded-pill border-0 shadow-sm ps-5 small"
                                style={{ height: '38px', backgroundColor: 'var(--vz-input-bg, #fff)' }}
                                placeholder={t("Search branch name...")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <div className="spinner-border text-[#C59253]" role="status">
                            <div className="visually-hidden">Loading...</div>
                        </div>
                    </div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="d-flex flex-column gap-3"
                    >
                        {filteredSites && filteredSites.length > 0 ? (
                            filteredSites.map((site) => {
                                const isExpanded = expandedSites.includes(site.site_id);
                                const spaces = spacesBySite[site.site_id] || [];
                                const isLoadingSpaces = loadingSpaces[site.site_id];

                                return (
                                    <motion.div
                                        key={site.site_id}
                                        variants={itemVariants}
                                        className="bg-card-theme rounded-3 shadow-sm border overflow-hidden"
                                    >
                                        {/* Collapsible Header */}
                                        <div
                                            className="group-header px-4 py-3 d-flex justify-content-between align-items-center"
                                            onClick={() => toggleSite(site.site_id)}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div
                                                    className={`transition-transform d-flex align-items-center justify-content-center text-muted`}
                                                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                                                >
                                                    <ChevronDown size={20} />

                                                </div>
                                                <div className="rounded-3 d-flex align-items-center justify-content-center"
                                                    style={{ width: '36px', height: '36px', overflow: 'hidden', backgroundColor: 'var(--vz-light)' }}>
                                                    {site.image_url ? (
                                                        <img src={site.image_url} alt="" className="w-100 h-100 object-fit-cover" />
                                                    ) : (
                                                        <LayoutGrid size={18} style={{ color: THEME_COLOR }} />
                                                    )}
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-bold">{site.name}</h6>
                                                    {!isExpanded && (
                                                        <span className="x-small text-muted">
                                                            {spaces.length > 0 ? `${spaces.length} Spaces` : t("Expand to view spaces")}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center gap-2">
                                                {isExpanded && (
                                                    <span className="badge bg-light text-muted fw-normal me-2 border">
                                                        {pagination?.totalItems} {t("Spaces")}
                                                    </span>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedSiteId(site.site_id);
                                                    }}
                                                    className="btn btn-sm rounded-pill fw-bold text-white shadow-sm d-flex align-items-center gap-2 px-3"
                                                    style={{ backgroundColor: THEME_COLOR, border: 'none' }}
                                                >
                                                    <Plus size={14} />
                                                    {t("Manage")}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expandable Content */}
                                        <AnimatePresence initial={false}>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    <div className="p-4 bg-light bg-opacity-10 border-top">
                                                        {isLoadingSpaces ? (
                                                            <div className="d-flex justify-content-center py-4">
                                                                <div className="spinner-border spinner-border-sm text-secondary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </div>
                                                        ) : spaces && spaces.length > 0 ? (
                                                            <div className="row g-3">
                                                                {spaces.map((space) => (
                                                                    <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={space.space_id}>
                                                                        <motion.div
                                                                            whileHover={{ y: -2 }}
                                                                            className="card h-100 border shadow-sm p-3 rounded-3"
                                                                        >
                                                                            <div className="d-flex flex-column h-100">
                                                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                                                    <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                                                                                        <h6 className="fw-bold text-body text-truncate mb-0 small">{space.name}</h6>
                                                                                        <span className="text-muted x-small">#{space.space_id}</span>
                                                                                    </div>
                                                                                    <div className="d-flex gap-1">
                                                                                        <button
                                                                                            className="btn btn-link btn-sm p-1 text-muted"
                                                                                            onClick={(e) => { e.stopPropagation(); handleQRSpace(space, site); }}
                                                                                            title="View QR"
                                                                                        >
                                                                                            <QrCode size={16} />
                                                                                        </button>
                                                                                        <button
                                                                                            className="btn btn-link btn-sm p-1 text-muted"
                                                                                            onClick={(e) => { e.stopPropagation(); handleEditSpace(space, site.site_id); }}
                                                                                            title="Edit"
                                                                                            disabled={loadingAction.type === 'edit' && loadingAction.id === space.space_id}
                                                                                        >
                                                                                            {loadingAction.type === 'edit' && loadingAction.id === space.space_id ? (
                                                                                                <div className="spinner-border text-[#C59253]" style={{ width: '20px', height: '20px' }} role="status">
                                                                                                    <span className="visually-hidden">Loading...</span>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <Edit2 size={16} />
                                                                                            )}
                                                                                        </button>
                                                                                        <button
                                                                                            className="btn btn-link btn-sm p-1 text-danger"
                                                                                            onClick={(e) => { e.stopPropagation(); handleDeleteSpace(space.space_id, site.site_id); }}
                                                                                            title="Delete"
                                                                                        >
                                                                                            <Trash2 size={16} />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="mt-auto pt-2 border-top border-light border-opacity-50">
                                                                                    {renderMenuTags(space, site.menus || [])}
                                                                                </div>
                                                                            </div>
                                                                        </motion.div>
                                                                    </div>
                                                                ))}

                                                                {/* Pagination / Add Space Card Inline */}
                                                                <div className="col-12 col-md-6 col-lg-4 col-xl-3">
                                                                    {hasMoreBySite[site.site_id] ? (
                                                                        <div
                                                                            className="card h-100 border border-dashed bg-white d-flex align-items-center justify-content-center cursor-pointer shadow-none hover-scale"
                                                                            style={{ minHeight: '100px', borderColor: THEME_COLOR }}
                                                                            onClick={(e) => { e.stopPropagation(); loadMoreSpaces(site.site_id); }}
                                                                        >
                                                                            {loadingMore[site.site_id] ? (
                                                                                <div className="spinner-border spinner-border-sm text-secondary" style={{ color: THEME_COLOR }} role="status">
                                                                                    <span className="visually-hidden">Loading...</span>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="text-center">
                                                                                    <ChevronDown size={18} style={{ color: THEME_COLOR }} />
                                                                                    <span className="d-block small fw-bold mt-1" style={{ color: THEME_COLOR }}>{t("Show More")}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div
                                                                            className="card h-100 border border-dashed d-flex align-items-center justify-content-center cursor-pointer shadow-none hover-scale"
                                                                            style={{ minHeight: '100px', borderColor: THEME_COLOR, backgroundColor: 'transparent' }}
                                                                            onClick={() => setSelectedSiteId(site.site_id)}
                                                                        >
                                                                            <div className="text-center">
                                                                                <Plus size={18} style={{ color: THEME_COLOR }} />
                                                                                <span className="d-block small fw-bold mt-1" style={{ color: THEME_COLOR }}>{t("Add Spaces")}</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center py-4">
                                                                <div className="bg-white p-3 rounded-circle shadow-sm d-inline-block mb-2">
                                                                    <LayoutGrid size={24} className="text-muted opacity-50" />
                                                                </div>
                                                                <h6 className="text-muted fw-bold small">{t("No spaces yet")}</h6>
                                                                <p className="text-muted x-small mb-3">{t("Start adding tables for this branch")}</p>
                                                                <button
                                                                    className="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 x-small"
                                                                    onClick={() => setSelectedSiteId(site.site_id)}
                                                                >
                                                                    {t("Setup Spaces")}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                );
                            })
                        ) : (
                            <div className="col-12 text-center py-5">
                                <div className="bg-white p-4 rounded-circle shadow-sm d-inline-block mb-3">
                                    <LayoutGrid size={32} className="text-muted opacity-50" />
                                </div>
                                <h5 className="text-muted fw-bold">{t("No branches found")}</h5>
                            </div>
                        )}
                    </motion.div>
                )}
            </div>

            {/* Manage Spaces View Overlay */}
            <AnimatePresence>
                {selectedSiteId && (
                    <ManageSpacesView
                        siteId={selectedSiteId}
                        onClose={() => {
                            setSelectedSiteId(null);
                            if (expandedSites.includes(selectedSiteId)) {
                                toggleSite(selectedSiteId);
                                setTimeout(() => toggleSite(selectedSiteId), 100);
                            }
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Edit Space Modal */}
            {editSpaceData && (
                <EditSpace
                    rowData={editSpaceData}
                    kitchenList={kitchenList}
                    menuList={menuList}
                    siteList={sitesData}
                    showEditSpaceModal={showEditSpaceModal}
                    toggleEditSpaceModal={async () => {
                        setShowEditSpaceModal(!showEditSpaceModal);
                        if (showEditSpaceModal) {
                            // Modal closed (potentially after save)
                            // Ideally passed success flag but we can just refresh
                            // Since EditSpace updates state directly, we just reload spaces for the site
                            const sid = editSpaceData.site_id;
                            setLoadingSpaces(prev => ({ ...prev, [sid]: true }));
                            const spaces = await getSpaces(sid, { page: 1, perPage: 100 });
                            setSpacesBySite(prev => ({ ...prev, [sid]: spaces || [] }));
                            setLoadingSpaces(prev => ({ ...prev, [sid]: false }));
                        }
                    }}
                />
            )}

            {/* QR Modal */}
            {qrData && (
                <QRmodal
                    info={qrData}
                    showQRCode={showQrModal}
                    toggleQRModal={() => setShowQrModal(!showQrModal)}
                    altImage={qrData.menu_site_image}
                />
            )}

            {/* Delete Modal */}
            <Modal isOpen={deleteModal.isOpen} toggle={() => setDeleteModal({ ...deleteModal, isOpen: !deleteModal.isOpen })} centered size="sm">
                <div className="p-4 text-center">
                    <div className="mx-auto bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <Trash2 className="text-danger" size={24} />
                    </div>
                    <h5 className="fw-bold mb-2">{t("Delete Space?")}</h5>
                    <p className="text-muted small mb-4">
                        {t("Are you sure you want to delete this space?")}
                        <br />
                        {t("This action cannot be undone.")}
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button color="light" className="border px-4" onClick={() => setDeleteModal({ ...deleteModal, isOpen: false })} disabled={loadingAction.type === 'delete_confirm'}>{t("Cancel")}</Button>
                        <Button color="danger" className="px-4" onClick={confirmDeleteSpace} disabled={loadingAction.type === 'delete_confirm'}>
                            {loadingAction.type === 'delete_confirm' ? <div className="spinner-border text-[#C59253]" style={{ width: '20px', height: '20px' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div> : t("Delete")}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MySpaces;
